import { useEffect, useRef } from 'react'
import { queryClient } from '@/lib/react-query'
import { hydrateAuthSession } from '@/lib/auth/hydrateFromAuthSession'
import { useAuthStore } from '@/store/useAuthStore'

/** Durée minimale d'absence avant revalidation au retour sur l'onglet. */
const HIDDEN_BEFORE_RESUME_MS = 5 * 60 * 1000
/** Anti-spam entre deux revalidations consécutives. */
const MIN_RESUME_INTERVAL_MS = 60 * 1000

/**
 * Au retour sur l'onglet après une longue absence, revalide GET /auth/session
 * (getAuthenticatedUser refresh les cookies expirés) et invalide les queries métier.
 */
export function useSessionResume(): void {
  const lastHiddenAtRef = useRef<number | null>(null)
  const lastResumeAtRef = useRef(0)
  const resumeInFlightRef = useRef(false)

  useEffect(() => {
    const handleResume = async () => {
      const { sessionHydrated, user, sessionTransitioning, sessionSilentSwap } =
        useAuthStore.getState()

      if (
        !sessionHydrated ||
        !user?.id ||
        sessionTransitioning ||
        sessionSilentSwap ||
        resumeInFlightRef.current
      ) {
        return
      }

      const now = Date.now()
      const hiddenDuration = lastHiddenAtRef.current
        ? now - lastHiddenAtRef.current
        : 0
      const sinceLastResume = now - lastResumeAtRef.current

      if (
        hiddenDuration < HIDDEN_BEFORE_RESUME_MS &&
        sinceLastResume < MIN_RESUME_INTERVAL_MS
      ) {
        return
      }

      resumeInFlightRef.current = true
      lastResumeAtRef.current = now

      try {
        const outcome = await hydrateAuthSession()

        if (outcome.status === 'authenticated') {
          await queryClient.invalidateQueries()
          return
        }

        // Réseau coupé / API 5xx : on ne sait pas si la session est encore valide.
        // Ne rien purger — l'onglet réveillé reprend souvent avant le réseau, et une
        // purge ici déconnecterait sur un simple blip. Prochain resume réessaiera.
        if (outcome.status === 'unavailable') {
          lastResumeAtRef.current = 0
          return
        }

        // Do not call /auth/logout here: the cookie may still be valid server-side,
        // logout would wipe it. Only the client state is cleared.
        const { clearStaleAuthState } = await import('@/lib/auth/clearStaleAuthState')
        await clearStaleAuthState({ callLogoutApi: false })
      } catch (error) {
        // Filet de sécurité : ce handler est appelé via `void`, une rejection
        // remonterait en unhandledrejection et polluerait l'error tracking.
        console.warn('[useSessionResume] resume failed', error)
      } finally {
        resumeInFlightRef.current = false
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now()
        return
      }
      if (document.visibilityState === 'visible') {
        void handleResume()
      }
    }

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void handleResume()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])
}
